/**
 * printReceipt — Receipt PDF generator (iter-218, Feb 2026)
 *
 * Same brand look as `printAssignedTask.js` (blue → orange gradient,
 * Cairo/Inter fonts, dirham inline icon, footer with website/phone/email)
 * — kept intentionally self-contained so we can iterate on receipt design
 * without touching the task-print flow.
 *
 * iter-289 (Feb 2026) — Reads live brand settings from
 * `/api/admin/receipt-studio/brand` (with a 5-minute in-module cache) so
 * the admin's edits propagate without a redeploy. Falls back to the
 * factory defaults if the fetch fails or the user is unauthenticated.
 *
 * Public API:
 *   printReceipt(receipt, { language })              — opens tab + native print
 *   downloadReceiptPDF(receipt, { language })        — html2pdf.js download
 *
 * `receipt` shape (matches backend `admin_receipts` doc):
 *   {
 *     receipt_no, task_id, request_no, payer_name, payer_phone,
 *     payer_email, amount_aed, currency, service_name_ar, service_name_en,
 *     created_at, created_by
 *   }
 */

const FACTORY_BRAND = {
  logo_url:         '/pdf-logo-cloudgm.png',
  accent_from:      '#3B9EDC',
  accent_to:        '#F5A55A',
  brand_name_ar:    'CLOUDGM FZ LLC',
  brand_name_en:    'CLOUDGM FZ LLC',
  footer_sites:     ['cloudgm.com', 'cloudgm.business'],
  footer_phone:     '+971 50 151 1917',
  footer_emails:    ['info@cloudgm.com', 'info@cloudgm.business'],
  footer_tagline_ar: 'شكراً لتعاملكم معنا',
  footer_tagline_en: 'Thank you for your business',
};

const BRAND_CACHE_TTL_MS = 5 * 60 * 1000;
let _brandCache = null;
let _brandCacheAt = 0;

const fetchBrand = async () => {
  const now = Date.now();
  if (_brandCache && (now - _brandCacheAt) < BRAND_CACHE_TTL_MS) return _brandCache;
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    const r = await fetch(`${base}/api/admin/receipt-studio/brand`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!r.ok) throw new Error(`brand fetch ${r.status}`);
    const data = await r.json();
    _brandCache = { ...FACTORY_BRAND, ...(data?.data || {}) };
    _brandCacheAt = now;
  } catch {
    _brandCache = FACTORY_BRAND;
    _brandCacheAt = now;
  }
  return _brandCache;
};

const escapeHtml = (v = '') =>
  String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' }[c]));

const fmtAmount = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (iso, isAr) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const opts = { year: 'numeric', month: 'long', day: '2-digit', hour: '2-digit', minute: '2-digit' };
    return d.toLocaleDateString(isAr ? 'ar-AE' : 'en-GB', opts);
  } catch {
    return String(iso);
  }
};

const buildFilename = (receipt) => {
  const no = String(receipt.receipt_no || 'receipt').replace(/[^A-Za-z0-9-]/g, '_');
  const payer = String(receipt.payer_name || '').trim().replace(/[<>:"/\\|?*\u0000-\u001F]/g, '').replace(/\s+/g, '_').slice(0, 60);
  const rawStatus = String(receipt.status || 'paid').toLowerCase();
  const prefix = rawStatus === 'paid' ? 'إيصال' : 'فاتورة';
  return `${prefix}_${no}${payer ? '_' + payer : ''}`;
};

const buildHtml = (receipt, isAr, opts = {}) => {
  const dir = isAr ? 'rtl' : 'ltr';
  const t   = (ar, en) => (isAr ? ar : en);
  const downloadMode = !!opts.downloadMode;
  const brand = opts.brand || FACTORY_BRAND;
  const LOGO_URL = brand.logo_url || FACTORY_BRAND.logo_url;
  const CONTACT = {
    sites:  brand.footer_sites  || FACTORY_BRAND.footer_sites,
    phone:  brand.footer_phone  || FACTORY_BRAND.footer_phone,
    emails: brand.footer_emails || FACTORY_BRAND.footer_emails,
  };
  const BRAND_FROM = brand.accent_from || FACTORY_BRAND.accent_from;
  const BRAND_TO   = brand.accent_to   || FACTORY_BRAND.accent_to;
  const BRAND_NAME = isAr ? (brand.brand_name_ar || brand.brand_name_en || '')
                          : (brand.brand_name_en || brand.brand_name_ar || '');
  const BRAND_TAG  = isAr ? (brand.footer_tagline_ar || '') : (brand.footer_tagline_en || '');
  const filenameBase = buildFilename(receipt);
  const originOK = typeof window !== 'undefined' && window.location && window.location.origin;
  const dh = (n) => `<span class="dh-price">${originOK
    ? `<img src="${window.location.origin}/currency/dirham-black.png" alt="AED" class="dh-icon" />`
    : ''}${fmtAmount(n)}</span>`;

  // iter-243 (Feb 2026) — status stamp overlay. Every receipt/invoice now
  // renders a diagonal "PAID / PARTIAL / UNPAID" watermark. Manual tasks
  // and closed company registrations default to "paid" (they only reach
  // the receipt stage after full collection). "Create Invoice" callers
  // pass the invoice's actual status.
  const rawStatus = String(receipt.status || 'paid').toLowerCase();
  const stampMap = {
    paid:           { ar: 'مدفوع',        en: 'PAID',    color: '#059669', border: '#10b981', bg: '#ecfdf5' },
    partially_paid: { ar: 'مدفوع جزئياً', en: 'PARTIAL', color: '#b45309', border: '#f59e0b', bg: '#fffbeb' },
    unpaid:         { ar: 'غير مدفوع',     en: 'UNPAID',  color: '#b91c1c', border: '#ef4444', bg: '#fef2f2' },
    cancelled:      { ar: 'ملغاة',          en: 'CANCELLED', color: '#525252', border: '#737373', bg: '#f5f5f5' },
    refunded:       { ar: 'مستردَّة',       en: 'REFUNDED',  color: '#6d28d9', border: '#8b5cf6', bg: '#faf5ff' },
  };
  const stamp = stampMap[rawStatus] || stampMap.paid;
  const heroStatusLabel = rawStatus === 'paid'
    ? t('مسدَّد بالكامل', 'Paid in full')
    : (isAr ? stamp.ar : stamp.en);
  const showAmountHero = rawStatus !== 'unpaid';   // hide "Amount received" hero for unpaid
  const docTitleAr = rawStatus === 'paid' ? 'إيصال قبض' : 'فاتورة';
  const docTitleEn = rawStatus === 'paid' ? 'Payment Receipt' : 'Invoice';
  const refLabelAr = rawStatus === 'paid' ? 'رقم الإيصال' : 'رقم الفاتورة';
  const refLabelEn = rawStatus === 'paid' ? 'Receipt No.' : 'Invoice No.';

  const serviceMainName = isAr
    ? (receipt.service_name_ar || receipt.service_name_en || '')
    : (receipt.service_name_en || receipt.service_name_ar || '');
  const serviceSubName = isAr
    ? (receipt.sub_service_name_ar || receipt.sub_service_name_en || '')
    : (receipt.sub_service_name_en || receipt.sub_service_name_ar || '');
  // iter-218c (Feb 2026) — Sub-service is now shown as its OWN dedicated row
  // under "تفاصيل الخدمة" (previously it was concatenated with the main
  // service which made it easy to miss). If there's no sub-service we omit
  // the row entirely.
  const serviceName = serviceMainName || '—';

  // iter-218b (Feb 2026) — Payment-method label (bank_transfer default).
  const paymentMethodMap = {
    bank_transfer: { ar: 'تحويل بنكي', en: 'Bank Transfer' },
    receipt:       { ar: 'إيصال قبض',  en: 'Receipt' },
    link:          { ar: 'رابط دفع',   en: 'Payment Link' },
    online:        { ar: 'دفع أونلاين', en: 'Online' },
  };
  const pmLabel = (paymentMethodMap[receipt.payment_method] || paymentMethodMap.bank_transfer);
  const paymentMethodLabel = isAr ? pmLabel.ar : pmLabel.en;

  // iter-218b — Use the fees collection date snapshot, NOT the print/render
  // date. Falls back to the receipt's own created_at for very old rows.
  const collectionIso = receipt.paid_at || receipt.created_at;

  return `<!doctype html>
<html lang="${isAr ? 'ar' : 'en'}" dir="${dir}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${escapeHtml(filenameBase)}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700;800&family=Inter:wght@400;600;700&display=swap');

  :root {
    --blue-500: ${BRAND_FROM};
    --blue-700: ${BRAND_FROM};
    --orange-500: ${BRAND_TO};
    --orange-700: ${BRAND_TO};
    --emerald: #10B981;
    --ink-900: #0f172a;
    --ink-600: #334155;
    --ink-400: #64748b;
    --ink-200: #e2e8f0;
    --paper:   #ffffff;
    --tint:    #f8fafc;
    --brand-grad: linear-gradient(90deg, var(--blue-500) 0%, var(--orange-500) 100%);
  }
  @page { size: A4; margin: 18mm 14mm 24mm 14mm; }
  html, body {
    margin: 0; padding: 0;
    background: var(--paper);
    color: var(--ink-900);
    font-family: ${isAr ? "'Cairo'" : "'Inter'"}, -apple-system, BlinkMacSystemFont, 'Segoe UI', Arial, sans-serif;
    font-size: 12px;
    line-height: 1.55;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .doc-wrapper { padding: 6mm 4mm; max-width: 200mm; margin: 0 auto; }

  /* iter-280 (Feb 2026) — Fix oversized AED icon in fee-table / body.
     The dh-icon was only sized inside .amount-hero .amt; anywhere else
     (fee-table cells, kv rows) it rendered at its natural PNG size and
     blew the page layout out. Default it globally to 12px, and let the
     hero override to 22px. */
  .dh-price {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    white-space: nowrap;
  }
  .dh-icon {
    height: 12px;
    width: auto;
    display: inline-block;
    vertical-align: middle;
    max-height: 1em;
  }

  /* Header — same as task print */
  .doc-header {
    display: flex; align-items: center; gap: 20px;
    padding: 16px 18px;
    border: 1px solid var(--ink-200);
    border-radius: 14px;
    background: var(--tint);
    position: relative;
    overflow: hidden;
  }
  .doc-header::before {
    content: ''; position: absolute; inset: 0 0 auto 0; height: 6px;
    background: var(--brand-grad);
  }
  .brand-logo { height: 72px; width: auto; display: block; flex-shrink: 0; }
  .doc-meta { flex: 1; }
  .doc-title { font-size: 20px; font-weight: 800; color: var(--ink-900); margin-bottom: 4px; }
  .doc-ref, .doc-date { font-size: 11px; color: var(--ink-600); }
  .doc-ref .ref-value { font-family: 'JetBrains Mono', 'Menlo', monospace; color: var(--blue-700); font-weight: 800; margin-inline-start: 6px; }
  .ref-label { color: var(--ink-400); font-weight: 600; }
  .doc-date { margin-top: 2px; }

  /* Receipt hero (large amount) */
  .amount-hero {
    margin-top: 16px;
    padding: 20px 24px;
    border-radius: 14px;
    background: linear-gradient(135deg, #ecfdf5 0%, #f8fafc 100%);
    border: 2px solid var(--emerald);
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 12px;
  }
  .amount-hero .lbl { font-size: 11px; text-transform: uppercase; letter-spacing: 0.8px; color: var(--emerald); font-weight: 800; margin-bottom: 4px; }
  .amount-hero .status { font-size: 13px; color: var(--ink-600); font-weight: 700; }
  .amount-hero .amt {
    font-size: 26px; font-weight: 900; color: var(--ink-900);
    font-variant-numeric: tabular-nums;
    display: inline-flex; align-items: center; gap: 6px;
  }
  .amount-hero .amt .dh-price { display: inline-flex; align-items: center; gap: 4px; }
  .amount-hero .amt .dh-icon { height: 22px; width: auto; display: inline-block; }

  /* Sections */
  .section { margin-top: 14px; page-break-inside: avoid; }
  .section-title {
    font-size: 12px; font-weight: 800; margin: 0 0 8px 0; padding: 6px 12px;
    background: var(--brand-grad); color: #fff; border-radius: 8px;
    display: inline-block;
  }
  .kv-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; }
  .kv {
    display: flex; align-items: center; justify-content: space-between;
    padding: 8px 12px; background: #fff;
    border: 1px solid var(--ink-200); border-radius: 8px; gap: 8px;
  }
  .kv .k { color: var(--ink-400); font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.4px; }
  .kv .v { color: var(--ink-900); font-weight: 700; font-size: 12px; text-align: end; }
  .kv.wide { grid-column: 1 / -1; }
  .mono { font-family: 'JetBrains Mono', 'Menlo', monospace; font-variant-numeric: tabular-nums; }

  /* Signature strip */
  .sig-strip {
    margin-top: 22px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }
  .sig-box {
    border: 1px dashed var(--ink-400);
    border-radius: 10px;
    padding: 30px 12px 12px 12px;
    text-align: center;
  }
  .sig-box .sig-lbl {
    font-size: 10.5px; color: var(--ink-400); text-transform: uppercase;
    letter-spacing: 0.5px; font-weight: 700;
  }
  .sig-box .sig-name { font-size: 12px; color: var(--ink-900); font-weight: 700; margin-top: 4px; }

  /* Footer — matches task print */
  .doc-footer {
    position: fixed; bottom: 8mm; inset-inline: 14mm;
    text-align: center;
    color: var(--ink-400);
    font-size: 10px;
  }
  .doc-footer .footer-bar { height: 3px; background: var(--brand-grad); border-radius: 3px; margin-bottom: 6px; }
  .doc-footer .footer-row { display: flex; justify-content: space-between; gap: 8px; align-items: center; flex-wrap: wrap; }
  .doc-footer .footer-line { margin-top: 2px; color: var(--ink-400); font-weight: 600; }
  @media screen { .doc-footer { position: static; margin-top: 30px; } }

  /* iter-243 — Status stamp (paid/partial/unpaid) */
  .status-stamp {
    position: absolute; top: 14px; inset-inline-end: 14px;
    padding: 6px 18px;
    border: 3px solid ${stamp.border};
    color: ${stamp.color};
    background: ${stamp.bg};
    font-weight: 900; font-size: 16px;
    letter-spacing: 2px;
    border-radius: 6px;
    text-transform: uppercase;
    transform: rotate(-8deg);
    box-shadow: 0 2px 6px rgba(0,0,0,0.08);
    z-index: 2;
  }
  .doc-header { position: relative; }

  /* iter-244 — Fee breakdown table + stages timeline (request report) */
  .fee-table { width: 100%; border-collapse: collapse; font-size: 12px; }
  .fee-table th, .fee-table td { padding: 8px 10px; border-bottom: 1px solid #e5e7eb; }
  .fee-table th { background: #f8fafc; color: #475569; text-align: start; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
  .fee-table tbody tr:last-child td { border-bottom: 0; }
  .fee-total-row td { font-weight: 900; background: #eff6ff; color: #1e3a8a; border-top: 2px solid var(--blue-500); }

  .stages-list { list-style: none; padding: 0; margin: 0; display: grid; gap: 10px; }
  .stage-item { display: flex; gap: 12px; padding: 10px 12px; border-radius: 10px; background: #f8fafc; border: 1px solid #e5e7eb; }
  .stage-num { flex: 0 0 28px; width: 28px; height: 28px; border-radius: 50%; background: linear-gradient(90deg, var(--blue-500), var(--orange-500)); color: #fff; font-weight: 900; font-size: 12px; display: flex; align-items: center; justify-content: center; }
  .stage-body { flex: 1; }
  .stage-title { font-weight: 800; color: #0f172a; font-size: 13px; }
  .stage-meta { color: #64748b; font-size: 11px; display: flex; flex-wrap: wrap; gap: 8px; margin-top: 3px; }
  .stage-note { color: #94a3b8; }

  /* Print bar (screen only) */
  .print-bar {
    position: sticky; top: 0; z-index: 20;
    display: flex; gap: 8px; justify-content: flex-end;
    padding: 10px 14px; background: #0f172a; color: #fff;
  }
  .print-bar button {
    padding: 8px 14px; border-radius: 8px; border: 0; font-size: 13px; font-weight: 700; cursor: pointer;
    background: linear-gradient(90deg, var(--blue-500) 0%, var(--orange-500) 100%);
    color: #fff;
  }
  .print-bar button.secondary { background: #334155; }
  @media print { .print-bar { display: none; } }
</style>
</head>
<body>
${downloadMode ? '' : `
  <div class="print-bar">
    <button onclick="window.print()">${t('طباعة', 'Print')}</button>
    <button class="secondary" onclick="window.close()">${t('إغلاق', 'Close')}</button>
  </div>`}
<div class="doc-wrapper">
  <header class="doc-header">
    <img src="${LOGO_URL}" alt="CloudGM FZ-LLC" class="brand-logo" />
    <div class="doc-meta">
      <div class="doc-title">${t(docTitleAr, docTitleEn)}</div>
      <div class="doc-ref">
        <span class="ref-label">${t(refLabelAr, refLabelEn)}:</span>
        <span class="ref-value">${escapeHtml(receipt.receipt_no)}</span>
      </div>
      <div class="doc-date">
        <span class="ref-label">${rawStatus === 'paid' ? t('تاريخ التحصيل', 'Collection date') : t('تاريخ الإصدار', 'Issue date')}:</span>
        <span>${fmtDate(collectionIso, isAr)}</span>
      </div>
      ${receipt.company_trn ? `<div class="doc-date" dir="ltr">
        <span class="ref-label">TRN</span>
        <span>${escapeHtml(receipt.company_trn)}</span>
      </div>` : ''}
    </div>
    <div class="status-stamp">${isAr ? stamp.ar : stamp.en}</div>
  </header>

  ${showAmountHero ? `<section class="amount-hero">
    <div>
      <div class="lbl">${t('المبلغ المُستلم', 'Amount received')}</div>
      <div class="status">${escapeHtml(heroStatusLabel)}</div>
    </div>
    <div class="amt">${dh(receipt.amount_aed)}</div>
  </section>` : `<section class="amount-hero" style="background:#fef2f2;border-color:#ef4444;">
    <div>
      <div class="lbl" style="color:#b91c1c;">${t('المبلغ المستحق', 'Amount due')}</div>
      <div class="status" style="color:#525252;">${escapeHtml(heroStatusLabel)}</div>
    </div>
    <div class="amt">${dh(receipt.amount_aed)}</div>
  </section>`}

  <section class="section">
    <h2 class="section-title">${t('بيانات الدافع', 'Payer details')}</h2>
    <div class="kv-grid">
      <div class="kv wide">
        <span class="k">${t('الاسم', 'Name')}</span>
        <span class="v">${escapeHtml(receipt.payer_name || '—')}</span>
      </div>
      ${receipt.payer_phone ? `
      <div class="kv">
        <span class="k">${t('رقم الهاتف', 'Phone')}</span>
        <span class="v mono" dir="ltr">${escapeHtml(receipt.payer_phone)}</span>
      </div>` : ''}
      ${receipt.payer_email ? `
      <div class="kv">
        <span class="k">${t('البريد الإلكتروني', 'Email')}</span>
        <span class="v mono" dir="ltr">${escapeHtml(receipt.payer_email)}</span>
      </div>` : ''}
      ${receipt.request_no ? `
      <div class="kv wide">
        <span class="k">${t('رقم الطلب', 'Request No.')}</span>
        <span class="v mono">${escapeHtml(receipt.request_no)}</span>
      </div>` : ''}
    </div>
  </section>

  <section class="section">
    <h2 class="section-title">${t('تفاصيل الخدمة', 'Service details')}</h2>
    <div class="kv-grid">
      <div class="kv wide">
        <span class="k">${t('الخدمة', 'Service')}</span>
        <span class="v">${escapeHtml(serviceName)}</span>
      </div>
      ${serviceSubName ? `
      <div class="kv wide">
        <span class="k">${t('الخدمة الفرعية', 'Sub-service')}</span>
        <span class="v">${escapeHtml(serviceSubName)}</span>
      </div>` : ''}
      <div class="kv">
        <span class="k">${t('العملة', 'Currency')}</span>
        <span class="v">${escapeHtml(receipt.currency || 'AED')}</span>
      </div>
      <div class="kv">
        <span class="k">${t('طريقة السداد', 'Payment method')}</span>
        <span class="v">${escapeHtml(paymentMethodLabel)}</span>
      </div>
    </div>
  </section>

  ${receipt.company_details ? `<section class="section">
    <h2 class="section-title">${t('بيانات الشركة', 'Company details')}</h2>
    <div class="kv-grid">
      ${receipt.company_details.name_ar ? `<div class="kv wide"><span class="k">${t('الاسم العربي', 'Arabic name')}</span><span class="v">${escapeHtml(receipt.company_details.name_ar)}</span></div>` : ''}
      ${receipt.company_details.name_en ? `<div class="kv wide"><span class="k">${t('الاسم الإنجليزي', 'English name')}</span><span class="v" dir="ltr">${escapeHtml(receipt.company_details.name_en)}</span></div>` : ''}
      ${receipt.company_details.license_number ? `<div class="kv"><span class="k">${t('رقم الرخصة', 'License No.')}</span><span class="v mono">${escapeHtml(receipt.company_details.license_number)}</span></div>` : ''}
      ${receipt.company_details.authority ? `<div class="kv"><span class="k">${t('جهة الإصدار', 'Authority')}</span><span class="v">${escapeHtml(receipt.company_details.authority)}</span></div>` : ''}
      ${receipt.company_details.legal_form ? `<div class="kv"><span class="k">${t('الشكل القانوني', 'Legal form')}</span><span class="v">${escapeHtml(receipt.company_details.legal_form)}</span></div>` : ''}
      ${receipt.company_details.license_type ? `<div class="kv"><span class="k">${t('نوع الرخصة', 'License type')}</span><span class="v">${escapeHtml(receipt.company_details.license_type)}</span></div>` : ''}
      ${receipt.company_details.issue_date ? `<div class="kv"><span class="k">${t('تاريخ الإصدار', 'Issue date')}</span><span class="v mono">${escapeHtml(receipt.company_details.issue_date)}</span></div>` : ''}
      ${receipt.company_details.expiry_date ? `<div class="kv"><span class="k">${t('تاريخ الانتهاء', 'Expiry date')}</span><span class="v mono">${escapeHtml(receipt.company_details.expiry_date)}</span></div>` : ''}
    </div>
  </section>` : ''}

  ${receipt.fee_breakdown && receipt.fee_breakdown.length ? `<section class="section">
    <h2 class="section-title">${t('تفصيل الرسوم والمبالغ', 'Fees & amounts breakdown')}</h2>
    <table class="fee-table">
      <thead>
        <tr>
          <th>${t('البند', 'Item')}</th>
          <th style="text-align:end;">${t('المبلغ (د.إ)', 'Amount (AED)')}</th>
        </tr>
      </thead>
      <tbody>
        ${receipt.fee_breakdown.map((it) => `
          <tr>
            <td>${escapeHtml(isAr ? (it.label_ar || it.label_en) : (it.label_en || it.label_ar))}</td>
            <td style="text-align:end;" class="mono">${dh(it.amount)}</td>
          </tr>`).join('')}
        ${receipt.fee_total !== undefined ? `
          <tr class="fee-total-row">
            <td>${t('الإجمالي', 'Total')}</td>
            <td style="text-align:end;" class="mono">${dh(receipt.fee_total)}</td>
          </tr>` : ''}
      </tbody>
    </table>
  </section>` : ''}

  ${receipt.stages_timeline && receipt.stages_timeline.length ? `<section class="section">
    <h2 class="section-title">${t('مراحل الطلب', 'Request stages')}</h2>
    <ol class="stages-list">
      ${receipt.stages_timeline.map((s, idx) => `
        <li class="stage-item">
          <div class="stage-num">${idx + 1}</div>
          <div class="stage-body">
            <div class="stage-title">${escapeHtml(isAr ? (s.label_ar || s.label_en || s.stage) : (s.label_en || s.label_ar || s.stage))}</div>
            <div class="stage-meta">
              ${s.date ? `<span>📅 ${fmtDate(s.date, isAr)}</span>` : ''}
              ${s.actor ? `<span>👤 ${escapeHtml(s.actor)}</span>` : ''}
              ${s.note ? `<span class="stage-note">— ${escapeHtml(s.note)}</span>` : ''}
            </div>
          </div>
        </li>`).join('')}
    </ol>
  </section>` : ''}
</div>

<div class="doc-footer">
  <div class="footer-bar"></div>
  ${BRAND_TAG ? `<div class="footer-row" style="font-weight:700;color:${BRAND_FROM};">${escapeHtml(BRAND_TAG)}</div>` : ''}
  <div class="footer-row">
    <span>🌐 ${CONTACT.sites.join(' • ')}</span>
    <span>📞 <span dir="ltr">${CONTACT.phone}</span></span>
    <span>✉ ${CONTACT.emails.join(' • ')}</span>
  </div>
  <div class="footer-line">${escapeHtml(BRAND_NAME) || 'CLOUD GM FZ-LLC'} — ${t('طُبع في', 'Printed on')} ${fmtDate(new Date().toISOString(), isAr)}</div>
</div>
</body>
</html>`;
};

// Print — opens a new tab with the printable receipt
export const printReceipt = async (receipt, opts = {}) => {
  if (!receipt) return;
  const isAr = (opts.language || 'ar').toLowerCase().startsWith('ar');
  const brand = await fetchBrand();
  const win = window.open('', '_blank', 'width=900,height=1100');
  if (!win) return;
  win.document.open();
  win.document.write(buildHtml(receipt, isAr, { downloadMode: false, brand }));
  win.document.close();
  // wait for fonts + logo, then auto-focus
  setTimeout(() => { try { win.focus(); } catch { /* noop */ } }, 600);
};

// Download PDF — same shielded flow as task download to avoid viewport jitter
export const downloadReceiptPDF = async (receipt, opts = {}) => {
  if (!receipt) return;
  const isAr = (opts.language || 'ar').toLowerCase().startsWith('ar');
  const brand = await fetchBrand();

  const html = buildHtml(receipt, isAr, { downloadMode: true, brand });

  const shield = document.createElement('div');
  shield.setAttribute('data-pdf-shield', '1');
  shield.style.cssText = 'position:fixed;inset:0;z-index:2147483000;background:rgba(0,0,0,0.55);display:flex;align-items:center;justify-content:center;color:#fff;font-family:sans-serif;font-size:14px';
  shield.innerHTML = `<div style="text-align:center">
    <div style="width:40px;height:40px;border:3px solid rgba(255,255,255,0.25);border-top-color:#fff;border-radius:50%;margin:0 auto 12px;animation:pdfSpin 0.9s linear infinite"></div>
    <div>${isAr ? 'جارٍ إنشاء الإيصال…' : 'Generating receipt…'}</div>
  </div><style>@keyframes pdfSpin{to{transform:rotate(360deg)}}</style>`;
  const prevOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
  document.body.appendChild(shield);

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-20000px';
  container.style.top  = '0';
  container.style.width = '210mm';
  container.style.background = '#ffffff';
  container.style.color = '#0f172a';
  container.style.fontFamily = isAr ? 'Cairo, Arial, sans-serif' : 'Inter, Arial, sans-serif';
  container.style.fontSize = '12px';
  container.style.lineHeight = '1.55';
  container.style.zIndex = '-1';
  container.style.contain = 'layout style paint';
  container.style.pointerEvents = 'none';
  container.innerHTML = html;
  container.querySelectorAll('.print-bar, script, title').forEach((el) => el.remove());
  document.body.appendChild(container);
  void container.offsetHeight;

  const injectedStyles = [];
  container.querySelectorAll('style').forEach((s) => {
    const clone = s.cloneNode(true);
    clone.setAttribute('data-pdf-injected', 'true');
    document.head.appendChild(clone);
    injectedStyles.push(clone);
  });

  const renderTarget = container.querySelector('.doc-wrapper') || container;

  try {
    if (document.fonts && typeof document.fonts.ready?.then === 'function') {
      await document.fonts.ready;
    }
    const imgs = Array.from(container.querySelectorAll('img'));
    await Promise.all(imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return Promise.resolve();
      return new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener('load',  done, { once: true });
        img.addEventListener('error', done, { once: true });
        setTimeout(done, 4000);
      });
    }));
    const html2pdfModule = await import('html2pdf.js');
    const html2pdf = html2pdfModule.default || html2pdfModule;

    const filename = `${buildFilename(receipt)}.pdf`;
    await html2pdf()
      .from(renderTarget)
      .set({
        margin:       [8, 8, 12, 8],
        filename,
        image:        { type: 'jpeg', quality: 0.94 },
        html2canvas:  { scale: 2, useCORS: true, allowTaint: true, letterRendering: true, backgroundColor: '#ffffff', logging: false },
        jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait', compress: true },
        pagebreak:    { mode: ['css', 'legacy', 'avoid-all'] },
      })
      .save();
  } finally {
    if (container.parentNode) container.parentNode.removeChild(container);
    injectedStyles.forEach((s) => s.parentNode && s.parentNode.removeChild(s));
    if (shield.parentNode) shield.parentNode.removeChild(shield);
    document.body.style.overflow = prevOverflow;
  }
};
